import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { assignLanes } from "@/components/save/career-graph-lanes";
import { AbstractTile } from "@/components/design/AbstractTile";
import { Select } from "@/components/design/Select";
import type { TobisCredit } from "@/lib/tobis";

// Filmografie im Zuschnitt des CareerGraph "Modern" (siehe
// components/save/CareerGraph.tsx): Trunk mit HEUTE-Pfeil, gefuellte Knoten fuer
// laufende und offene Kreise fuer abgeschlossene Eintraege, Fork-Kurve zur
// Verzweigung, Merge zurueck zum Trunk und gestrichelte Verbindung zur Karte.
// Konstanten und Pfadformen sind absichtlich identisch, damit beide Graphen
// gleich aussehen.
//
// Lane-Zuweisung aus career-graph-lanes.assignLanes, also dieselbe Regel wie im
// Profil (ADR-0023). Sonderfall tobis: Rollen kennen nur Jahre. Das traegt, weil
// monthsOf() eine reine Jahresangabe als Januar dieses Jahres liest. Wichtig ist
// die Unterscheidung Film gegen Serie: ein Film ist ein Punktereignis (Ende
// gleich Anfang, nie "laufend"), eine Serie ohne Enddatum laeuft weiter und
// ueberschneidet damit alles Spaetere.

const GAP = 14;
const ARROW_TOP = 20;
const TOP_PAD = 42;
const NODE_Y = 11;
const LANE_DX = 18;
const TRUNK_X = 16;
const CARD_H = 76;
const INK_LINE = "color-mix(in srgb, var(--color-ink) 70%, transparent)";

interface GraphRow {
  credit: TobisCredit;
  start: string;
  end: string;
  active: boolean;
  lane?: number;
}

// Ein Graph-Knoten kann mehrere Credits buendeln, wenn sie Punktereignisse im
// selben Jahr auf derselben Lane sind (typisch: ein produktiver Komponist mit
// vier Scores in einem Jahr). Laufende Rollen und Mehrjahres-Spannen bleiben
// einzeln, weil ihr Datumsbereich eigene Aussagekraft hat.
interface GraphGroup {
  items: GraphRow[];
  lane: number;
  active: boolean;
  year: string | null;
}

// null heisst "kein Buendelungs-Kandidat": laufende Rollen (active) und
// Eintraege mit echter Zeitspanne (start !== end, z.B. eine Serie ueber
// mehrere Jahre) behalten ihre eigene Zeile.
function itemYear(item: GraphRow): string | null {
  if (item.active || item.start !== item.end) return null;
  return item.start.slice(0, 4) || null;
}

function clusterByYear(items: readonly GraphRow[]): GraphGroup[] {
  const groups: GraphGroup[] = [];
  for (const item of items) {
    const year = itemYear(item);
    const lane = item.lane ?? 0;
    const prev = groups[groups.length - 1];
    if (prev && year !== null && prev.year === year && prev.lane === lane) {
      prev.items.push(item);
    } else {
      groups.push({ items: [item], lane, active: item.active, year });
    }
  }
  return groups;
}

function rangeOf(c: TobisCredit): { start: string; end: string; active: boolean } {
  const start = c.premiered?.slice(0, 7) ?? (c.year === null ? "" : String(c.year));
  const end = c.ended?.slice(0, 7) ?? (c.medium === "movie" ? start : "");
  // Ein Film laeuft nie, eine Serie ohne Enddatum schon.
  return { start, end, active: c.medium !== "movie" && !c.ended };
}

// Sortierschluessel: laufende Rollen stehen ueber allem, danach nach Endjahr.
// Ein Film endet im Erscheinungsjahr, weil rangeOf() Ende gleich Anfang setzt.
function sortValue(r: GraphRow): number {
  if (r.active) return Number.POSITIVE_INFINITY;
  const year = Number(r.end.slice(0, 4));
  return Number.isFinite(year) ? year : Number.NEGATIVE_INFINITY;
}

// Ab dieser Lane-Zahl bietet der Graph einen Jahresfilter an. "Aktive"
// (laufende) Rollen ueberlappen sich per Definition IMMER gegenseitig, weil
// sie kein Enddatum haben - ein Synchronsprecher mit vielen gleichzeitig
// laufenden Serien kann so leicht auf Dutzende Lanes kommen, unabhaengig vom
// Jahr. Der Filter grenzt VOR assignLanes ein, verkleinert also das Problem,
// loest es aber nicht vollstaendig: auch ein einzelnes Jahr kann bei so einer
// Karriere noch zweistellige Parallelrollen haben.
const YEAR_FILTER_LANE_THRESHOLD = 12;

// Welche Jahre eine Rolle "beruehrt" - bei einer laufenden Rolle bis zum
// aktuellen Jahr, sonst bis zum Endjahr (ein Film: Ende gleich Anfang).
function yearsTouchedBy(credit: TobisCredit, nowYear: number): number[] {
  const { start, end, active } = rangeOf(credit);
  const startYear = start ? Number(start.slice(0, 4)) : null;
  if (startYear === null || !Number.isFinite(startYear)) return [];
  const endYear = active ? nowYear : end ? Number(end.slice(0, 4)) : startYear;
  if (!Number.isFinite(endYear) || endYear < startYear) return [startYear];
  const years: number[] = [];
  for (let y = startYear; y <= endYear; y++) years.push(y);
  return years;
}

function Arrow({ x, color }: { x: number; color: string }) {
  return (
    <path
      d={`M ${x - 3} ${ARROW_TOP + 5} L ${x} ${ARROW_TOP} L ${x + 3} ${ARROW_TOP + 5}`}
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
  );
}

function TitleIcon({ credit, index }: { credit: TobisCredit; index: number }) {
  const shape = "h-16 w-11 shrink-0 overflow-hidden rounded";
  if (credit.coverUrl) {
    return <img src={credit.coverUrl} alt="" loading="lazy" className={`${shape} object-cover`} />;
  }
  return (
    <div className={shape}>
      <AbstractTile
        variant={["dots", "lines", "grid", "rings"][index % 4] as "dots"}
        hue={(index * 53) % 360}
        className="h-full w-full"
      />
    </div>
  );
}

export function TobisFilmographyGraph({ credits }: { credits: readonly TobisCredit[] }) {
  const intl = useIntl();
  const nowYear = new Date().getFullYear();

  // Ungefiltert gemessen, nicht am sichtbaren Ausschnitt: sonst wuerde der
  // Filter, kaum ausgewaehlt, sich selbst wieder ausblenden, sobald er die
  // Lane-Zahl unter den Schwellenwert drueckt.
  const totalLaneCount = useMemo(() => {
    const items = credits.map((credit) => ({ credit, ...rangeOf(credit) }));
    return assignLanes(items);
  }, [credits]);

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const credit of credits) {
      for (const year of yearsTouchedBy(credit, nowYear)) set.add(year);
    }
    return [...set].sort((a, b) => b - a);
  }, [credits, nowYear]);

  const [selectedYear, setSelectedYear] = useState("");
  const showYearFilter = totalLaneCount > YEAR_FILTER_LANE_THRESHOLD;

  const visibleCredits = useMemo(() => {
    if (!showYearFilter || !selectedYear) return credits;
    const year = Number(selectedYear);
    return credits.filter((credit) => yearsTouchedBy(credit, nowYear).includes(year));
  }, [credits, showYearFilter, selectedYear, nowYear]);

  const { rows, laneCount } = useMemo(() => {
    // Sortiert nach ENDE absteigend, laufende zuerst. Das weicht bewusst vom
    // Profil ab, das nach Beginn sortiert: bei einer Filmografie zaehlt, was die
    // Person zuletzt gemacht hat, und eine Serie, die 2022 endete, ist aktueller
    // als eine, die 2019 endete, auch wenn sie frueher begann.
    //
    // Die Reihenfolge entscheidet mit, welche Rolle den Hauptpfad bekommt:
    // assignLanes gibt der ersten Rolle Lane 0, alle zeitlich ueberschneidenden
    // wandern auf Verzweigungen. So traegt die laengerlaufende Rolle den Trunk.
    const items: GraphRow[] = visibleCredits
      .map((credit) => ({ credit, ...rangeOf(credit) }))
      .sort((a, b) => sortValue(b) - sortValue(a) || b.start.localeCompare(a.start));
    return { rows: items, laneCount: assignLanes(items) };
  }, [visibleCredits]);

  // Buendelung erst NACH der Lane-Zuweisung: assignLanes sieht weiter jeden
  // Credit einzeln (korrekte Ueberlappungspruefung), erst die Anzeige fasst
  // benachbarte Punktereignisse im selben Jahr auf derselben Lane zusammen.
  const groups = useMemo(() => clusterByYear(rows), [rows]);

  // Kartenhoehen messen, damit Knoten und Linien mitwandern, wenn ein Eintrag
  // mehr Text hat (Mehrfachrollen, Sprechrolle, mehrere Titel im Jahr).
  // Gleiche Mechanik wie im CareerGraph.
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [heights, setHeights] = useState<number[]>([]);
  useLayoutEffect(() => {
    const measure = () => {
      const next = groups.map((_, i) => cardRefs.current[i]?.offsetHeight || CARD_H);
      setHeights((prev) =>
        prev.length === next.length && prev.every((h, i) => h === next[i]) ? prev : next,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    cardRefs.current.slice(0, groups.length).forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [groups]);

  const laneX = (lane: number) => TRUNK_X + lane * LANE_DX;
  const cardH = (i: number) => heights[i] ?? CARD_H;
  const ys: number[] = [];
  let acc = TOP_PAD;
  for (let i = 0; i < groups.length; i++) {
    ys.push(acc + NODE_Y);
    acc += cardH(i) + GAP;
  }
  const totalH = acc;
  const graphW = laneX(laneCount - 1) + 12;
  const bottomY = ys[groups.length - 1] ?? TOP_PAD;
  const lastIsBranch = (groups[groups.length - 1]?.lane ?? 0) !== 0;
  const trunkBottomY = lastIsBranch ? bottomY + GAP : bottomY;

  function span(credit: TobisCredit): string {
    const from = credit.premiered?.slice(0, 4) ?? (credit.year === null ? "" : String(credit.year));
    const to = credit.ended?.slice(0, 4) ?? "";
    if (from && to && from !== to) {
      return intl.formatMessage(
        { id: "tobis.person.yearRange", defaultMessage: "{from} to {to}" },
        { from, to },
      );
    }
    if (from && !to && credit.medium !== "movie") {
      return intl.formatMessage(
        { id: "tobis.person.sinceYear", defaultMessage: "{year} until today" },
        { year: from },
      );
    }
    return from || to;
  }

  // Rolle/Job unter dem Titel — geteilt zwischen der Einzel- und der
  // gebuendelten Darstellung, damit beide dieselbe Prioritaet (Rolle vor
  // Crew-Job) und denselben Mehrfachrollen-Zusatz zeigen.
  function creditMeta(credit: TobisCredit) {
    if (credit.characterName) {
      return (
        <div className="mt-0.5 truncate text-[13px] text-ink/55">
          {intl.formatMessage(
            { id: "tobis.person.asRole", defaultMessage: "as {role}" },
            { role: credit.characterName },
          )}
          {credit.roleCount > 1
            ? intl.formatMessage(
                { id: "tobis.person.moreRoles", defaultMessage: " and {count} more roles" },
                { count: credit.roleCount - 1 },
              )
            : ""}
        </div>
      );
    }
    if (credit.jobs.length > 0) {
      // Reine Crew-Rolle (Regie/Drehbuch, kein Schauspiel-Credit auf diesem
      // Titel): sonst bliebe unter dem Titel nichts stehen.
      return <div className="mt-0.5 truncate text-[13px] text-ink/55">{credit.jobs.join(", ")}</div>;
    }
    return null;
  }

  if (rows.length === 0 && !showYearFilter) return null;

  return (
    <>
      {showYearFilter ? (
        <div className="mb-4 w-36">
          <Select
            value={selectedYear}
            onChange={setSelectedYear}
            options={years.map((y) => ({ value: String(y), label: String(y) }))}
            placeholder={intl.formatMessage({
              id: "tobis.person.allYears",
              defaultMessage: "All years",
            })}
          />
        </div>
      ) : null}
      {rows.length === 0 ? null : (
        <div
          className="relative grid min-w-0 gap-1 overflow-x-auto"
          style={{ gridTemplateColumns: `${graphW}px 1fr`, minHeight: totalH }}
        >
          <div className="relative">
        <div className="absolute left-0 right-0 top-0 flex items-center gap-2 text-[9px] tracking-[0.3em] text-accent">
          <span className="h-[1.5px] flex-1 bg-accent" />
          <span>
            <FormattedMessage id="save.careerGraph.todayMarker" defaultMessage="TODAY" />
          </span>
          <span className="h-[1.5px] flex-1 bg-accent" />
        </div>

        <svg
          width={graphW}
          height={totalH}
          viewBox={`0 0 ${graphW} ${totalH}`}
          aria-hidden="true"
          className="absolute left-0 top-0"
        >
          {groups.map((r, i) => {
            const x = laneX(r.lane);
            const color = r.active ? "var(--color-accent)" : INK_LINE;
            if (r.lane === 0) {
              return (
                <g key={`lane-${i}`}>
                  <line
                    x1={x}
                    y1={r.active ? ARROW_TOP : ys[i]}
                    x2={x}
                    y2={trunkBottomY}
                    stroke={color}
                    strokeWidth="1.5"
                  />
                  {r.active ? <Arrow x={x} color={color} /> : null}
                </g>
              );
            }
            const fork = (
              <path
                d={`M ${TRUNK_X} ${ys[i]! + GAP} Q ${x} ${ys[i]! + GAP} ${x} ${ys[i]}`}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
              />
            );
            if (r.active) {
              return (
                <g key={`lane-${i}`}>
                  {fork}
                  <line x1={x} y1={ARROW_TOP} x2={x} y2={ys[i]} stroke={color} strokeWidth="1.5" />
                  <Arrow x={x} color={color} />
                </g>
              );
            }
            return (
              <g key={`lane-${i}`}>
                {fork}
                <path
                  d={`M ${x} ${ys[i]} Q ${x} ${ys[i]! - GAP} ${TRUNK_X} ${ys[i]! - GAP - 4}`}
                  fill="none"
                  stroke={color}
                  strokeWidth="1.5"
                />
              </g>
            );
          })}

          {groups.map((r, i) => {
            const x = laneX(r.lane);
            const color = r.active ? "var(--color-accent)" : INK_LINE;
            return (
              <g key={`node-${i}`}>
                <line
                  x1={x}
                  y1={ys[i]}
                  x2={graphW - 2}
                  y2={ys[i]}
                  stroke={color}
                  strokeOpacity={0.3}
                  strokeWidth="1"
                  strokeDasharray="2 3"
                />
                <circle
                  cx={x}
                  cy={ys[i]}
                  r={6}
                  fill={r.active ? "var(--color-accent)" : "var(--color-page)"}
                  stroke={color}
                  strokeWidth="2"
                />
              </g>
            );
          })}
        </svg>
      </div>

      <ol className="min-w-0" style={{ paddingTop: TOP_PAD }}>
        {groups.map((g, i) => {
          const first = g.items[0]!;
          return (
            <li
              key={`${first.credit.tvmazeId}-${first.credit.characterName}-${g.items.length}`}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              style={{ marginBottom: GAP }}
            >
              {g.items.length === 1 ? (
                // Ganze Zeile (Poster UND Text) ist EIN Link statt nur der
                // Titeltext, mit leichtem Hover-Highlight - Zuschnitt aus
                // ResultStep.tsx (match.result), dieselbe -mx/px-Kompensation
                // haelt die optische Ausrichtung beim Hover-Hintergrund.
                <Link
                  to={`/tobis/titles/${first.credit.tvmazeId ?? ""}`}
                  className="-mx-2 flex flex-1 gap-3 rounded-md px-2 py-1.5 transition hover:bg-ink/[0.06]"
                >
                  <TitleIcon credit={first.credit} index={i} />
                  <div className="min-w-0">
                    <div className="text-[12px] text-ink/40 tabular-nums">{span(first.credit)}</div>
                    <div className="text-[14px] text-ink">{first.credit.titleName}</div>
                    {creditMeta(first.credit)}
                    {first.credit.isSelf || first.credit.isVoice ? (
                      <div className="text-[12px] text-ink/40">
                        {first.credit.isSelf
                          ? intl.formatMessage({
                              id: "tobis.person.asSelf",
                              defaultMessage: "as themselves",
                            })
                          : intl.formatMessage({
                              id: "tobis.person.voice",
                              defaultMessage: "voice role",
                            })}
                      </div>
                    ) : null}
                  </div>
                </Link>
              ) : (
                // Mehrere Punktereignisse im selben Jahr auf derselben Lane
                // (z.B. vier Filmmusik-Scores 2025): Jahr einmal, Titel
                // nebeneinander statt eine Zeile je Titel. Dieselbe
                // Hover-Highlight-Konvention wie oben, nur je Karte statt je Zeile.
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-ink/40 tabular-nums">{span(first.credit)}</div>
                  <div className="mt-1 flex flex-wrap gap-3">
                    {g.items.map((item, j) => (
                      <Link
                        key={`${item.credit.tvmazeId}-${item.credit.characterName}-${j}`}
                        to={`/tobis/titles/${item.credit.tvmazeId ?? ""}`}
                        className="-m-1.5 flex w-24 flex-col gap-1 rounded-md p-1.5 transition hover:bg-ink/[0.06]"
                      >
                        <TitleIcon credit={item.credit} index={i + j} />
                        <span className="truncate text-[13px] text-ink">{item.credit.titleName}</span>
                        {creditMeta(item.credit)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
        </div>
      )}
    </>
  );
}
